import axiosClient from "utils/axiosClient.util";
import {
  IZnsCampaign,
  IZnsCampaignListParams,
  IZnsCampaignListResponse,
  IZnsCampaignResponse,
  IUploadRecipientsResponse,
  IOlCampaignListParams,
  IOlCampaignListResponse,
  IZnsCampaignConfigurationListParams,
  IZnsCampaignConfigurationListResponse,
  UpdateZnsCampaignConfigurationPayload,
} from "../types/znsCampaign";
import { toastEmitter } from "utils/toast.util";

const BASE_URL = "/api/ZnsCampaign/v1";
const OL_CAMPAIGN_URL = "/api/Campaign/v1";

/**
 * GET /api/ZnsCampaign/v1
 * Fetches the list of ZNS campaigns with pagination and filters.
 */
const getZnsCampaigns = (
  params?: IZnsCampaignListParams
): Promise<IZnsCampaignListResponse> => {
  return axiosClient.get(BASE_URL, { params });
};

/**
 * GET /api/ZnsCampaign/v1/all/configurations
 * Paginated ZNS campaign configurations for reporting / admin lists.
 */
const getZnsCampaignConfigurations = (
  params?: IZnsCampaignConfigurationListParams,
): Promise<IZnsCampaignConfigurationListResponse> => {
  return axiosClient.get(`${BASE_URL}/all/configurations`, { params });
};

/**
 * GET /api/Campaign/v1
 * Fetches the list of OL campaigns with pagination and filters.
 */
const getOlCampaigns = (
  params?: IOlCampaignListParams
): Promise<IOlCampaignListResponse> => {
  return axiosClient.get(OL_CAMPAIGN_URL, {
    params: { PlatformType: "OpenLoyalty", ...params },
  });
};

/**
 * POST /api/ZnsCampaign/v1
 * Creates a new ZNS campaign.
 */
const createZnsCampaign = (payload: IZnsCampaign): Promise<string> => {
  return axiosClient.post(BASE_URL, payload);
};

/**
 * POST /api/ZnsCampaign/v1/{id}/notifications/generate
 * Generates notifications for a ZNS campaign.
 */
const generateNotifications = async (id: string): Promise<void> => {
  try {
    await axiosClient.post(`${BASE_URL}/${id}/notifications/generate`);
  } catch (err: any) {
    const msg =
      err?.message || err?.Message || "Failed to generate notifications.";
    toastEmitter.show(msg, "error");
    throw err;
  }
};

/**
 * GET /api/ZnsCampaign/v1/{id}
 * Fetches a single ZNS campaign by ID.
 */
const getZnsCampaignById = (id: string): Promise<IZnsCampaign> => {
  return axiosClient.get(`${BASE_URL}/${id}`);
};

/**
 * PUT /api/ZnsCampaign/v1/{id}
 * Updates an existing ZNS campaign by ID.
 */
const updateZnsCampaign = (
  id: string,
  payload: IZnsCampaign
): Promise<IZnsCampaign> => {
  return axiosClient.put(`${BASE_URL}/${id}`, payload);
};

/**
 * PUT /api/ZnsCampaign/v1/{znsCampaignId}/configurations/{configurationId}
 * Request body includes configurationId and fields to update (e.g. isCurrent).
 */
const updateZnsCampaignConfiguration = (
  znsCampaignId: string,
  payload: UpdateZnsCampaignConfigurationPayload,
): Promise<unknown> => {
  return axiosClient.put(
    `${BASE_URL}/${znsCampaignId}/configurations/${payload.configurationId}`,
    payload,
  );
};

/**
 * PUT /api/ZnsCampaign/v1/{znsCampaignId}/status
 * Body: plain text status value (e.g. "DEACTIVE").
 */
const updateZnsCampaignStatus = (
  znsCampaignId: string,
  status: string,
): Promise<unknown> => {
  return axiosClient.put(`${BASE_URL}/${znsCampaignId}/status`, status, {
    headers: { "Content-Type": "application/json-patch+json" },
  });
};

/**
 * DELETE /api/ZnsCampaign/v1/{id}
 * Deletes a ZNS campaign by ID.
 */
const deleteZnsCampaign = (id: string): Promise<void> => {
  return axiosClient.delete(`${BASE_URL}/${id}`);
};

/**
 * POST /api/ZnsCampaign/v1/recipients/upload
 * Uploads a CSV file of recipients for a given ZNS template.
 */
const uploadRecipients = async (
  znsTemplateId: string,
  file: File,
): Promise<IUploadRecipientsResponse> => {
  const formData = new FormData();
  formData.append("ZnsTemplateId", znsTemplateId);
  formData.append("File", file);
  try {
    const response = await axiosClient.post<IUploadRecipientsResponse>(
      `${BASE_URL}/recipients/upload`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );

    const data = (response as any).data || response;

    if (data.hasErrors) {
      const msg = `Upload completed with ${data.errorCount} errors.`;
      toastEmitter.show(msg, "warning");
      throw {
        message: msg,
        errorFilePath: data.errorFilePath,
      };
    }

    return data;
  } catch (err: any) {
    if (err.errorFilePath) throw err;

    const msg =
      err?.message || err?.Message || "Upload failed. Please try again.";
    toastEmitter.show(msg, "error");
    throw err;
  }
};

const downloadErrorFile = (path: string): Promise<Blob> => {
  return axiosClient.get(`/api/File/v1`, {
    params: { path },
    responseType: "blob",
  });
};

/**
 * GET /api/ZnsCampaign/v1/conditions
 * Fetches the conditions for a ZNS campaign.
 */
const getZnsCampaignConditions = (): Promise<any[]> => {
  return axiosClient.get(`${BASE_URL}/conditions`);
};

const ZnsCampaignService = {
  getZnsCampaigns,
  getZnsCampaignConfigurations,
  getOlCampaigns,
  createZnsCampaign,
  getZnsCampaignById,
  updateZnsCampaign,
  updateZnsCampaignConfiguration,
  updateZnsCampaignStatus,
  deleteZnsCampaign,
  uploadRecipients,
  generateNotifications,
  downloadErrorFile,
  getZnsCampaignConditions,
};

export default ZnsCampaignService;
